import React from 'react';
import { constantToLabel, ucWords } from '@ecosconnect/core/src/helpers/string';
import { gql, useQuery } from '@apollo/client';
import { Box, Flex, Text } from 'rebass';
import { assemblyCompliantReasonStyle } from '../BackflowAssembly/assemblyTestStatus';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { useStoreFilters } from '../../hooks/useStoredFilters';
import {
  AssemblyFilters,
  defaultFilters,
} from '../../pages/assemblies/FilterBarAssembly';

const COMPLIANCE_QUERY = gql`
  query AssemblyCompliance {
    compliance: backflowAssemblies(
      where: { status: "ACTIVE" }
      aggregate: [{ col: "id", type: DISTINCT, as: "backflowAssemblies" }]
      groupBy: ["compliantReason"]
      orderBy: [
        { col: "compliant", dir: DESC }
        { col: "agg.backflowAssemblies", dir: DESC }
      ]
      limit: null
    ) {
      compliantReason
      agg
    }
  }
`;

type ComplianceData = {
  compliance: {
    compliantReason:
      | 'PASSING'
      | 'PENDING'
      | 'FAILING'
      | 'INSTALLATION'
      | 'PAST_DUE';
    agg: {
      backflowAssemblies: number;
    };
  }[];
};

type AssemblyComplianceWidgetProps = {
  width: number;
  height: number;
};

const AssemblyComplianceWidget = ({
  width,
  height,
}: AssemblyComplianceWidgetProps): JSX.Element => {
  const history = useHistory();
  const mode = useMode();
  const storeFilters = useStoreFilters<AssemblyFilters>('backflowAssemblies');

  const { data: complianceData } = useQuery<ComplianceData>(COMPLIANCE_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const max = complianceData
    ? Math.max(
        ...complianceData.compliance.map(
          (value) => value.agg.backflowAssemblies
        )
      )
    : 0;

  return (
    <>
      {complianceData && width && (
        <>
          <Flex flexDirection="column" flexGrow={1} overflowY="auto">
            {complianceData.compliance.map((row) => (
              <Flex
                height="20%"
                minHeight="35px"
                flexShrink={0}
                justifyContent="center"
                key={row.compliantReason}
                flexDirection="column"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => {
                  storeFilters({
                    ...defaultFilters,
                    compliantReason: [row.compliantReason],
                  });

                  history.push(`/${mode}/assemblies`);
                }}
              >
                <Flex justifyContent="space-between" mb={1}>
                  <Text variant="body2">
                    {constantToLabel(row.compliantReason)}
                  </Text>
                  <Text variant="body2" pr={1} fontWeight="bold">
                    {row.agg.backflowAssemblies}
                  </Text>
                </Flex>
                <Box
                  sx={{
                    borderRadius: 'circle',
                    height: 12,
                    bg:
                      'status' +
                      ucWords(
                        assemblyCompliantReasonStyle(row.compliantReason as any)
                      ),
                    width: (row.agg.backflowAssemblies / max) * 100 + '%',
                  }}
                />
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </>
  );
};

export default AssemblyComplianceWidget;
